// Cognito auth config — injected at build time via webpack DefinePlugin.
// Values are set per stage in configuration/webpack.config.js.
declare const COGNITO_USER_POOL_ID: string;
declare const COGNITO_CLIENT_ID: string;
declare const COGNITO_DOMAIN: string;

export const AUTH_CONFIG = {
  userPoolId: COGNITO_USER_POOL_ID,
  clientId: COGNITO_CLIENT_ID,
  cognitoDomain: COGNITO_DOMAIN,
  redirectUri: typeof window !== 'undefined' ? `${window.location.origin}/callback` : '',
  logoutUri: typeof window !== 'undefined' ? `${window.location.origin}/` : '',
};

// --- PKCE helpers (OAuth 2.1 required for public clients) ---

const base64UrlEncode = (bytes: Uint8Array): string =>
  btoa(Array.from(bytes, b => String.fromCharCode(b)).join(''))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

const generateCodeVerifier = (): string => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
};

const generateCodeChallenge = async (verifier: string): Promise<string> => {
  const data = new TextEncoder().encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
};

// --- Auth URLs ---

export const getLoginUrl = async (): Promise<string> => {
  const verifier = generateCodeVerifier();
  const challenge = await generateCodeChallenge(verifier);
  sessionStorage.setItem('pkce_code_verifier', verifier);
  return (
    `https://${AUTH_CONFIG.cognitoDomain}/oauth2/authorize?` +
    `client_id=${AUTH_CONFIG.clientId}&response_type=code&scope=openid+profile+email` +
    `&redirect_uri=${encodeURIComponent(AUTH_CONFIG.redirectUri)}` +
    `&code_challenge=${challenge}&code_challenge_method=S256`
  );
};

export const getLogoutUrl = () =>
  `https://${AUTH_CONFIG.cognitoDomain}/logout?` +
  `client_id=${AUTH_CONFIG.clientId}&logout_uri=${encodeURIComponent(AUTH_CONFIG.logoutUri)}`;

export const exchangeCodeForTokens = async (code: string) => {
  const codeVerifier = sessionStorage.getItem('pkce_code_verifier');
  if (!codeVerifier) {
    throw new Error('PKCE code verifier not found — please restart the login flow.');
  }
  sessionStorage.removeItem('pkce_code_verifier');
  const res = await fetch(`https://${AUTH_CONFIG.cognitoDomain}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: AUTH_CONFIG.clientId,
      code,
      redirect_uri: AUTH_CONFIG.redirectUri,
      code_verifier: codeVerifier,
    }),
  });
  if (!res.ok) throw new Error('Token exchange failed');
  return res.json() as Promise<{ id_token: string; access_token: string; refresh_token: string }>;
};

// --- Helpers ---

const decodeBase64Url = (str: string): string => {
  const base64 = str.replace(/-/g, "+").replace(/_/g, "/");
  return atob(base64);
};

// --- Token storage ---

export const getStoredToken = () => sessionStorage.getItem('beacon_id_token');
export const setStoredTokens = (idToken: string) => sessionStorage.setItem('beacon_id_token', idToken);
export const clearStoredTokens = () => {
  sessionStorage.removeItem('beacon_id_token');
  sessionStorage.removeItem('pkce_code_verifier');
};

export const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(decodeBase64Url(token.split('.')[1]));
    if (typeof payload.exp !== 'number') return true;
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
};

// --- User info ---

// SDE has full access, FinOps has read-only business views
export type UserRole = 'SDE' | 'FinOps' | 'Reader';

export const getUserRole = (groups: string[]): UserRole => {
  if (groups.includes('SDE')) return 'SDE';
  if (groups.includes('FinOps')) return 'FinOps';
  return 'Reader';
};

export const getUserFromToken = (token: string): { email: string; name: string; alias: string; groups: string[]; role: UserRole } | null => {
  try {
    const payload = JSON.parse(decodeBase64Url(token.split('.')[1]));
    const alias = payload['identities']?.[0]?.['userId'] || payload['cognito:username']?.split('_').pop() || '';
    const ldap = payload['custom:ldap_groups'];
    let groups: string[] = [];
    if (ldap) {
      try { groups = JSON.parse(ldap); } catch { groups = ldap.split(',').map((g: string) => g.trim()).filter(Boolean); }
    } else {
      groups = (payload['cognito:groups'] || []).filter((g: string) => !g.includes('_federate-'));
    }
    return { email: payload.email || '', name: payload.name || '', alias, groups, role: getUserRole(groups) };
  } catch {
    return null;
  }
};
